import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BrandingContextValue } from '../types/branding.types';
import { fetchBranding, DEFAULT_BRANDING } from '../services/branding.service';
import { applyBrandingToRoot } from '../utils/css-injector';
import { BrandingContext } from './branding-context';

interface BrandingProviderProps {
  children: React.ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
  });

  const resolvedBranding = data?.branding ?? DEFAULT_BRANDING;

  useEffect(() => {
    applyBrandingToRoot(resolvedBranding);
  }, [resolvedBranding]);

  const value = useMemo<BrandingContextValue>(() => ({
    branding: resolvedBranding,
    isLoading,
    isCustomBranded: resolvedBranding.organizationName !== DEFAULT_BRANDING.organizationName,
    isNotFound: data?.notFound ?? false,
  }), [resolvedBranding, isLoading, data?.notFound]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
